import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Dog } from "../../types";
import { useAuth } from "../../contexts/AuthContext";
import { InvitationPreview, InvitationType, acceptInvitation, getDogs, previewInvitation } from "../../utils/api";

const DESTINATIONS: Record<InvitationType, string> = {
  dashboard: "/sessions",
  "dashboard-roster": "/proposals",
  structure: "/sessions",
  "dog-trainer": "/management",
  "dog-owner": "/management",
};

export const JoinPage: React.FC = () => {
  const { token = "" } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [error, setError] = useState("");
  const [dogs, setDogs] = useState<Dog[]>([]);
  const [dogId, setDogId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    previewInvitation(token)
      .then(async p => {
        if (cancelled) return;
        setPreview(p);
        if (p.requiresDog) {
          const myDogs = await getDogs();
          if (!cancelled) {
            setDogs(myDogs);
            setDogId(myDogs[0]?._id || "");
          }
        }
      })
      .catch(err => !cancelled && setError(err?.response?.data?.message || "Invitation invalide, expirée ou révoquée."));
    return () => { cancelled = true; };
  }, [token]);

  const handleAccept = async () => {
    setSubmitting(true);
    try {
      const result = await acceptInvitation(token, preview?.requiresDog ? dogId : undefined);
      toast.success("Invitation acceptée !");
      navigate(result.type === "dashboard-roster" && user?.role.includes("trainer") ? `/sessions/${result.targetId}` : DESTINATIONS[result.type] || "/home", { replace: true });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Impossible d'accepter l'invitation.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-md p-4">
      <Card>
        <CardHeader>
          <CardTitle>Invitation</CardTitle>
          <CardDescription>{preview ? preview.label : error || "Vérification du lien..."}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {preview && (
            <>
              {preview.createdBy && <p className="text-sm text-muted-foreground">Envoyée par <strong>{preview.createdBy}</strong>. Valable jusqu'au {new Date(preview.expiresAt).toLocaleDateString("fr-FR")}.</p>}
              {preview.requiresDog && (
                dogs.length ? (
                  <div className="space-y-1">
                    <label htmlFor="join-dog" className="text-sm font-medium">Votre chien concerné</label>
                    <select id="join-dog" className="h-9 w-full rounded-md border bg-background px-2 text-sm" value={dogId} onChange={e => setDogId(e.target.value)}>
                      {dogs.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
                    </select>
                  </div>
                ) : (
                  <p className="text-sm text-destructive">Vous devez d'abord ajouter un chien pour accepter cette invitation.</p>
                )
              )}
              <Button className="w-full" disabled={submitting || (preview.requiresDog && !dogId)} onClick={handleAccept}>Accepter</Button>
            </>
          )}
          <Button variant="ghost" className="w-full" onClick={() => navigate("/home")}>Retour à l'accueil</Button>
        </CardContent>
      </Card>
    </div>
  );
};
